import {
    Box,
    CircularProgress,
    Fab,
    Grid,
    IconButton,
    Paper,
    styled,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PopupForm from "./PopupForm";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { getPopupList } from "../../slice/PopupListSlice";

const COLUMNS = [
    { id: "name", label: "Name" },
    { id: "status", label: "Status" },
    { id: "is_active", label: "Active?" },
    { id: "target_platform", label: "Target Platform" },
    { id: "target_users", label: "Target Users" },
    { id: "start_at", label: "Start At" },
    { id: "end_at", label: "End At" },
    { id: "max_popups", label: "Max. Popups" },
    { id: "on_click_action", label: "On Click" },
];

const PopupTable = () => {
    const dispatch = useDispatch();

    const isLoading = useSelector(state => state.popupList?.isLoading);
    const [order, setOrder] = useState("asc")
    const [orderBy, setOrderBy] = useState("")
    const [page, setPage] = useState(0);
    const popupList = useSelector(state => state.popupList?.data?.data)
    const [popupFormOpen, setPopupFormOpen] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(30);
    const [selectedData, setSelectedData] = useState(null);

    const StyledFab = styled(Fab)({ zIndex: 1, color: "#fff", background: "#467CEB", "&:hover": { background: "#467CEB" }});

    useEffect(() => {
        dispatch(getPopupList());
    }, [dispatch]);

    const sortedData = Array.isArray(popupList)
        ? [...popupList]
        : [];

    sortedData.sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (typeof aValue === "string" && typeof bValue === "string") {
            return order === "asc"
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        } 
        return order === "asc" ? aValue - bValue : bValue - aValue;
    });

    function handleEditFormOpen(data) {
        setPopupFormOpen(true);
        setSelectedData(data);
    };

    function handlePageChange(_event, newPage) {
        setPage(newPage);
    };

    function handlePopupFormClose(isChanged) {
        setPopupFormOpen(false);
        setSelectedData(null);
        if (isChanged) {
            dispatch(getPopupList());
        }
    }; 

    function handleRowsPerPageChange(event) {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    function handleSort(property) {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    }

    function renderCellContent(value, columnId) {
        if (columnId === "is_active") {
            return value ? "Yes" : "No";
        } else if (columnId === "start_at" || columnId === "end_at") {
            return new Date(value).toISOString().split("T")[0];
        } else if (columnId === "image_url") {
            let filename = "";
            try {
                filename = value ? new URL(value).pathname.split("/").pop() : "";
            } catch {
                filename = value ? value.split("/").pop() : "";
            }
            return (
                <a href={value} target="_blank" rel="noopener noreferrer">
                    {filename}
                </a>
            )
        };
        return value;
    }

    function renderLoading() {
        return (
        <Grid sx={{ position: "absolute", top: "50%", left: "50%" }}>
            <CircularProgress />
        </Grid>
        );
    }

    return (isLoading ? renderLoading()
    :
    <>
    <Box style={{ boxShadow: "none" }} sx={{ height: "calc(100vh - 265px)", width: "100%" }}>
        <Box sx={{ p: 2, boxSizing: "border-box", width: "100%", display: "table", tableLayout: "fixed" }} >
            <TableContainer component={Paper} style={{ width: "100%", boxShadow: "none", maxHeight: "470px" }} >
                <Table stickyHeader style={{ height: "100%" }} >
                <TableHead>
                <TableRow>
                    {COLUMNS.map((row) => (
                        <TableCell
                            key={row.id}
                            sortDirection={orderBy === row.id ? order : false}
                            style={{ fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", textTransform: "uppercase", position: "sticky", backgroundColor: "#EFF4FF", zIndex: 1, padding: "10px 5px"}}
                        >
                            <TableSortLabel active={orderBy === row.id} direction={orderBy === row.id ? order : "asc"} onClick={() => handleSort(row.id)} >
                                {row.label}
                            </TableSortLabel>
                        </TableCell>
                    ))}
                    {/* Last column for edit button */}
                    {( 
                        <TableCell style={{ fontSize: "12px", fontWeight: "600", textTransform: "uppercase", position: "sticky", top: "0", right: "0", backgroundColor: "#EFF4FF", boxShadow: "-4px 0px 8px 0px rgba(152, 162, 179, 0.24)", zIndex: 1, padding: "10px 5px" }} /> 
                    )}
                </TableRow>
                </TableHead>
                <TableBody>
                {sortedData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                    <TableRow key={row.id}>
                        {COLUMNS.map((col) => (
                            <TableCell key={col.id} style={{ padding: "10px 5px" }}>
                                {renderCellContent(row[col.id], col.id)}
                            </TableCell>
                        ))}
                        {/* Last content for edit button */}
                        {
                            <TableCell style={{ fontSize: "12px", fontWeight: "600", textTransform: "uppercase", position: "sticky", right: 0, backgroundColor: "#EFF4FF", boxShadow: "-4px 0px 8px 0px rgba(152, 162, 179, 0.24)", zIndex: 999, padding: "10px 5px", }} >
                                <IconButton
                                    sx={{ position: "absolute", top: 0, right: 0, color: "var(--light-mode-gray-100, #001233)", }}
                                    aria-label="edit"
                                    onClick={() => handleEditFormOpen(row)}
                                >
                                    <EditIcon />
                                </IconButton>
                            </TableCell>
                        }
                    </TableRow>
                    ))}
                </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[30, 60, 90]}
                component="div"
                count={sortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
            />
        </Box>
        <Grid position="fixed" color="primary" sx={{ bottom: "30px", left: "100px" }} onClick={()=> handleEditFormOpen(null)} >
            <StyledFab color="#467CEB" background="#467CEB" >
                <AddIcon sx={{ fontSize: "30px" }} />
            </StyledFab>
        </Grid> 
    </Box>

    <PopupForm data={selectedData} isOpen={popupFormOpen} onClose={(isChanged) => handlePopupFormClose(isChanged)} />
    </>
    )
}

export default PopupTable